import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { MessageSquare, Sparkles, Clock, Loader2, Crown, BookOpen, ListChecks, Target, Calendar, LineChart, ArrowRight } from "lucide-react";
import CoachDrawer from "@/components/CoachDrawer";

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const min = Math.floor((now - then) / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString();
}

const WELCOME_ACTIONS = [
  { icon: BookOpen, label: "Explain a topic", prompt: "Explain photosynthesis in simple words with an analogy." },
  { icon: Target, label: "Help me solve a question", prompt: "Help me solve this step by step: A ball is thrown up at 10 m/s. How high does it go?" },
  { icon: ListChecks, label: "Create a quiz", prompt: "Create a 10-question medium-difficulty quiz about cell division." },
  { icon: Calendar, label: "Prepare me for an exam", prompt: "Help me build a 7-day study plan for MCAT biology." },
  { icon: LineChart, label: "Find my weak topics", prompt: "Look at my practice history and tell me what topics I should review." },
  { icon: Sparkles, label: "Give me practice questions", prompt: "Give me 5 medium-difficulty biochemistry practice questions." },
];

export default function CoachHistory() {
  const [sessions, setSessions] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [drawer, setDrawer] = useState({ open: false, topicId: null, topicName: null, prefill: null });
  const [locked, setLocked] = useState(null); // { message } when 402 pro_only

  const load = () => {
    Promise.all([
      api.get("/coach/sessions"),
      api.get("/subjects"),
    ])
      .then(([sRes, subRes]) => {
        setSessions(sRes.data);
        setSubjects(subRes.data);
        setLocked(null);
      })
      .catch((e) => {
        const d = e?.response?.data?.detail;
        if (e?.response?.status === 402 && d?.code === "pro_only") {
          setLocked({ message: d.message });
        }
        setSessions([]);
      });
  };

  useEffect(() => { load(); }, []);

  const openThread = (s) => {
    setDrawer({ open: true, topicId: s.topic_id, topicName: s.topic_name, prefill: null });
  };

  const openWithPrompt = (prompt) => {
    setDrawer({ open: true, topicId: null, topicName: null, prefill: prompt });
  };

  if (sessions === null) {
    return (
      <div className="flex items-center gap-2 py-16 justify-center" data-testid="coach-history-loading">
        <Loader2 className="animate-spin" size={18} /> Loading your coach history…
      </div>
    );
  }

  if (locked) {
    return (
      <div className="max-w-3xl mx-auto" data-testid="coach-history-pro-lock">
        <div className="pm-card p-8 text-center">
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: "#c98a3b22" }}>
            <Crown size={28} style={{ color: "#c98a3b" }} />
          </div>
          <div className="pm-badge mb-3 inline-flex" style={{ background: "#c98a3b22", color: "#c98a3b" }}>
            <Sparkles size={12} className="mr-1.5" /> Pro-only feature
          </div>
          <h1 className="font-display text-3xl font-bold mb-3">Meet Your AI Study Coach</h1>
          <p className="text-base mb-6 max-w-xl mx-auto" style={{ color: "var(--text-secondary)" }}>
            {locked.message}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6 max-w-lg mx-auto text-left">
            {WELCOME_ACTIONS.map((a) => {
              const Icon = a.icon;
              return (
                <div
                  key={a.label}
                  className="px-3 py-2 rounded-lg inline-flex items-center gap-2 text-sm border"
                  style={{ borderColor: "var(--border)", color: "var(--text-secondary)" }}
                >
                  <Icon size={14} style={{ color: "var(--primary)" }} />
                  {a.label}
                </div>
              );
            })}
          </div>
          <Link to="/app/plans" className="pm-btn-primary inline-flex items-center gap-2" data-testid="coach-history-upgrade-btn">
            <Crown size={14} /> Upgrade to Pro
          </Link>
        </div>
      </div>
    );
  }

  // Group by subject_id
  const groups = {};
  sessions.forEach((s) => {
    const key = s.subject_id || "general";
    if (!groups[key]) groups[key] = [];
    groups[key].push(s);
  });
  const groupOrder = Object.keys(groups).sort((a, b) => (a === "general" ? -1 : b === "general" ? 1 : a.localeCompare(b)));
  const subjectLabel = (k) => {
    if (k === "general") return "General questions";
    const s = subjects.find((x) => x.id === k);
    if (s?.name) return s.name;
    return k.charAt(0).toUpperCase() + k.slice(1).replace(/-/g, " ");
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="coach-history-page">
      <div className="mb-8">
        <div className="pm-badge mb-2" style={{ background: "var(--muted)", color: "var(--primary)" }}>
          <Sparkles size={12} className="mr-1.5" /> AI Study Coach · Pro
        </div>
        <h1 className="font-display text-4xl font-bold" data-testid="coach-history-heading">
          {sessions.length === 0 ? "Meet Your AI Study Coach" : "Coach conversations"}
        </h1>
        <p className="mt-2 text-lg" style={{ color: "var(--text-secondary)" }}>
          {sessions.length === 0
            ? "Your personal AI tutor is here to help you understand concepts, practice questions, prepare for exams, and improve the topics you find difficult."
            : "Every thread you've had with the AI tutor — revisit that killer explanation weeks later."}
        </p>
      </div>

      {sessions.length === 0 ? (
        <div className="pm-card p-6" data-testid="coach-history-welcome">
          <div className="font-display font-bold mb-3">Start with a quick action</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {WELCOME_ACTIONS.map((a) => {
              const Icon = a.icon;
              return (
                <button
                  key={a.label}
                  onClick={() => openWithPrompt(a.prompt)}
                  className="px-3 py-3 rounded-lg text-left inline-flex items-center gap-2.5 text-sm border hover:bg-[var(--muted)]"
                  style={{ borderColor: "var(--border)", color: "var(--text)" }}
                  data-testid={`coach-welcome-action-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <Icon size={16} style={{ color: "var(--primary)" }} />
                  <span className="flex-1">{a.label}</span>
                  <ArrowRight size={14} style={{ color: "var(--text-muted)" }} />
                </button>
              );
            })}
          </div>
          <div className="mt-4 text-xs text-center" style={{ color: "var(--text-muted)" }}>
            Or open the Coach on any topic from the topic page.{" "}
            <Link to="/app/subjects" className="underline" style={{ color: "var(--primary)" }}>Browse subjects →</Link>
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          {groupOrder.map((sid) => (
            <section key={sid} data-testid={`coach-history-group-${sid}`}>
              <h2 className="font-display font-bold text-xl mb-3">{subjectLabel(sid)}</h2>
              <div className="space-y-2">
                {groups[sid].map((s) => (
                  <button
                    key={s.session_id}
                    onClick={() => openThread(s)}
                    className="w-full pm-card pm-card-hover p-4 text-left"
                    data-testid={`coach-history-item-${s.session_id}`}
                  >
                    <div className="flex items-center justify-between mb-1 gap-3">
                      <div className="font-display font-bold text-base truncate">{s.topic_name}</div>
                      <div className="text-xs shrink-0 inline-flex items-center gap-1" style={{ color: "var(--text-muted)" }}>
                        <Clock size={11} /> {timeAgo(s.last_ts)}
                      </div>
                    </div>
                    <p className="text-sm line-clamp-2" style={{ color: "var(--text-secondary)" }}>
                      "{s.preview}"
                    </p>
                    <div className="flex items-center gap-2 mt-2 text-xs" style={{ color: "var(--text-muted)" }}>
                      <MessageSquare size={11} />
                      {s.message_count} message{s.message_count === 1 ? "" : "s"}
                    </div>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <CoachDrawer
        open={drawer.open}
        onClose={() => { setDrawer({ open: false, topicId: null, topicName: null, prefill: null }); load(); }}
        topicId={drawer.topicId}
        topicName={drawer.topicName}
        prefill={drawer.prefill}
      />
    </div>
  );
}
